// @noflow
import addMobileNavHelperListeners from '@/helpers/MobileNavHelper'
import lazyLoad from '@/modules/lazyLoad'
import initI18n from '@/packs/localisation'
import type { Language } from '@/packs/localisation'
import Intercom, { show } from '@intercom/messenger-js-sdk'
import i18next from 'i18next'
import React from 'react'
import ReactDOM from 'react-dom'
import 'scroll-behavior-polyfill'

import App from '@/components/contact_us'
import LastBox from '@/components/contact_us/components/LastBox'
import { initCookieConsent } from '@/components/cookie_consent'
import { ErrorCard } from '@/components/elements/atoms/ErrorCard'
import Text from '@/components/elements/atoms/Text'
import { initFooterLeadCapture } from '@/components/shared/elements/FooterLeadCapture/FooterLeadCapture'

import { toValidCSRFToken } from '@/shared_types/csrfToken'

const handleDixaCTACallBack = (): void => {
  const handleDixaCTA = setInterval(() => {
    const dixaButton = document.getElementById('dixa-chat-btn')
    const contactUsTitle = document.getElementById('contact-us-title')
    const dixaWidget = document.getElementsByClassName(
      'dixa-messenger-wrapper'
    )[0] as HTMLElement

    const intercomButton = document.getElementsByClassName(
      'intercom-lightweight-app-launcher'
    )[0] as HTMLElement

    if (dixaWidget && dixaButton && intercomButton) {
      intercomButton.style.display = 'none'
    }

    if (!dixaWidget && dixaButton && contactUsTitle) {
      dixaButton.classList.remove('btn-updated--disabled')
      dixaButton.removeAttribute('disabled')
      dixaButton.innerText = i18next.t('contacts:dixa_button_message')
      dixaButton.addEventListener('click', () => {
        window.scrollTo({
          behavior: 'smooth',
          top: contactUsTitle.offsetTop - 80
        })
      })
      clearInterval(handleDixaCTA)
    } else if (dixaButton) {
      dixaButton.classList.remove('btn-updated--disabled')
      dixaButton.removeAttribute('disabled')
      dixaButton.innerText = i18next.t('contacts:dixa_button_online')

      dixaButton.addEventListener('click', () => {
        window._dixa_.invoke('setWidgetOpen', true)

        /**
         * Handle the scenario where window.analytics.track is not present (in the
         * case of a customer having an ad-blocker etc)
         */
        if (window.analytics && window.analytics.track) {
          const eventName = 'Contact Us CTA Chat With Us Online Clicked'
          window.analytics.track(eventName)
        }
      })

      clearInterval(handleDixaCTA)
    }
  }, 3000)
}

const renderLastBox = (): void => {
  const lastBoxSection = document.getElementById('last-box')

  if (lastBoxSection) {
    const { dataset } = lastBoxSection
    const lastBoxId = dataset.lastBoxId as string
    const deliveryDate = JSON.parse(dataset.deliveryDate as string)
    const consumedByDate = new Date(
      JSON.parse(dataset.consumedByDate as string)
    )
    consumedByDate.setDate(consumedByDate.getDate() + 3)
    const deliveryStatus = dataset.deliveryStatus as string
    // const hasPreviousOrderIssues = dataset.hasPreviousOrderIssues === 'true'
    if (new Date() <= consumedByDate && deliveryStatus !== 'exception') {
      const lastBoxComponent = (
        <LastBox
          lastBoxId={lastBoxId}
          deliveryStatus={deliveryStatus}
          deliveryDate={new Date(deliveryDate)}
        />
      )
      ReactDOM.render(lastBoxComponent, lastBoxSection)
    }
  }
}

const renderErrorDetails = (): void => {
  const errorDetailsSection = document.getElementById('error-details')
  const errorDetailsStringified = new URLSearchParams(
    window.location.search
  ).get('errorDetails')
  const errorDetails = errorDetailsStringified
    ? JSON.parse(errorDetailsStringified)
    : null

  if (errorDetailsSection && errorDetails && errorDetails.message) {
    const errorDetailsComponent = (
      <>
        <Text
          variant="display24"
          text="error_state.error_other.contact_us_title"
          namespace="account"
          align={'left'}
        />
        <ErrorCard
          message={errorDetails.message}
          location={errorDetails.location}
        />
      </>
    )
    errorDetailsSection.classList.remove('hidden')
    ReactDOM.render(errorDetailsComponent, errorDetailsSection)
  }
}
const handleIntercomCTACallBack = (
  app_id: string,
  user_id?: string,
  name?: string,
  email?: string,
  created_at?: number,
  user_hash?: string
) => {
  if (user_id && name && email && created_at && user_hash) {
    Intercom({
      app_id,
      user_id,
      name,
      email,
      created_at,
      user_hash
    })
  } else {
    Intercom({
      app_id
    })
  }
  const chatButton = document.getElementById('chat-btn')

  if (!chatButton) {
    throw new Error('Could not load chat button')
  }

  chatButton.classList.remove('btn-updated--disabled')
  chatButton.removeAttribute('disabled')
  chatButton.innerText = i18next.t('contacts:dixa_button_online')
  chatButton.addEventListener('click', () => {
    show()
  })

  const dixaWidget = document.getElementsByClassName(
    'dixa-messenger-wrapper'
  )[0] as HTMLElement

  if (dixaWidget) {
    dixaWidget.remove()
  }
}

const handleCopyEmailButton = (): void => {
  const copyEmailButton = document.getElementById('copy-email-button')
  if (copyEmailButton) {
    copyEmailButton.addEventListener('click', () => {
      const emailAddress = copyEmailButton.dataset.clipboardText
      if (emailAddress) {
        navigator.clipboard
          .writeText(emailAddress)
          .then(() => {
            copyEmailButton.innerText = i18next.t(
              'contacts:email_address_copied'
            )
            setTimeout(() => {
              copyEmailButton.innerText = i18next.t(
                'contacts:email_address_copy'
              )
            }, 2000)
          })
          .catch((error) => {
            console.error('Failed to copy email address:', error)
            copyEmailButton.innerText = i18next.t(
              'contacts:email_address_copy_error'
            )
            setTimeout(() => {
              copyEmailButton.innerText = i18next.t(
                'contacts:email_address_copy'
              )
            }, 2000)
          })
      } else {
        console.error('Email address not found in button data attribute')
        copyEmailButton.innerText = i18next.t(
          'contacts.email_address_copy_error'
        )
        setTimeout(() => {
          copyEmailButton.innerText = i18next.t('contacts.email_address_copy')
        }, 2000)
      }
    })
  }
}

document.addEventListener('DOMContentLoaded', async (): Promise<void> => {
  addMobileNavHelperListeners()
  lazyLoad.update()

  const userData = document.getElementById('user-data')
  if (!userData) {
    throw new Error('Could not load user data')
  }

  const contactUsHelpCenterButton = document.getElementById(
    'contact-us-help-center-button'
  )
  if (!contactUsHelpCenterButton) {
    throw new Error('Could not load contact us help center button')
  }

  const {
    dataset: {
      shouldSeeIntercom,
      language,
      trackingId,
      intercomAppId,
      intercomUserHash,
      name,
      email,
      createdAt
    }
  } = userData

  const preferredLanguage = language || 'en'

  initI18n(preferredLanguage as Language)

  handleCopyEmailButton()

  if (shouldSeeIntercom === 'true' && intercomAppId) {
    handleIntercomCTACallBack(
      intercomAppId,
      trackingId,
      name,
      email,
      createdAt ? parseInt(createdAt) : undefined,
      intercomUserHash
    )
  } else {
    handleDixaCTACallBack()

    const contactFormPlaceholderElement =
      document.getElementById('contact-form')
    if (!contactFormPlaceholderElement) {
      throw new Error('Could not load contactFormPlaceholderElement')
    }

    contactUsHelpCenterButton.addEventListener('click', (): void => {
      /**
       * Handle the scenario where window.analytics.track is not present (in the
       * case of a customer having an ad-blocker etc)
       */
      if (window.analytics && window.analytics.track) {
        const eventName = 'Contact Us CTA Visit Our Help Centre Clicked'
        window.analytics.track(eventName)
      }
    })

    const { dataset } = contactFormPlaceholderElement

    const csrfToken = dataset.csrfToken
    const token = dataset.token ?? null
    const recaptchaSiteKey = dataset.recaptchaSiteKey as string
    const parsedUserId = parseInt(dataset.userId ?? '')
    const userId = isNaN(parsedUserId) ? null : parsedUserId

    const validCSRFToken = csrfToken && toValidCSRFToken(csrfToken)

    if (!validCSRFToken) {
      throw new Error(`CSRF Token is invalid ${csrfToken}`)
    }

    const component = (
      <App
        token={token}
        csrfToken={validCSRFToken}
        userId={userId}
        recaptchaSiteKey={recaptchaSiteKey}
      />
    )
    ReactDOM.render(component, contactFormPlaceholderElement)
    renderLastBox()
  }
  renderErrorDetails()
  initCookieConsent()
  initFooterLeadCapture()
})
